import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Wifi, WifiOff, Camera, RotateCcw, KeyRound, LogOut, UserPlus } from "lucide-react";
import BrandMark from "../components/BrandMark";
import ConfidenceBadge from "../components/ConfidenceBadge";
import WebcamCapture from "../components/WebcamCapture";
import FaceEnrollmentFlow from "../components/FaceEnrollmentFlow";
import {
  API_BASE, deviceApi, getDeviceToken, setDeviceSession, getDeviceMeta,
  clearDeviceSession, PEOPLE_ENGINE_URL,
} from "../lib/api";

/**
 * Presence Terminal — CAPTURE ONLY.
 *
 * Truth boundary:
 *   Presence captures attendance.
 *   People Engine decides attendance/payroll meaning.
 *   Control Room is not part of Presence.
 *
 * The kiosk has zero authority over attendance:
 *   - cannot approve, reject, override
 *   - cannot accept a manager login or PIN
 *   - cannot read or display payroll status
 *   - never receives a trusted outlet_id on the wire (it lives in the JWT)
 *
 * First launch shows the pairing screen. A manager generates a code in the
 * People Engine; Presence redeems it for a `presence_device_token` bound to
 * one outlet. Re-pair requires explicit unpair.
 */

const STATES = {
  PAIRING: "PAIRING",
  TOKEN_REJECTED: "TOKEN_REJECTED",
  IDLE: "IDLE",
  SELECT: "SELECT",
  CAPTURE: "CAPTURE",
  SCANNING: "SCANNING",
  RESULT: "RESULT",
  ENROLL: "ENROLL",
};

// If pairing succeeded < this many ms ago and we then get a 401 from Presence,
// the most likely cause is a secret/claim-shape mismatch between People
// Engine and Presence. Show a VISIBLE error instead of silently looping back
// to the pairing screen.
const JUST_PAIRED_WINDOW_MS = 60_000;

export default function Terminal() {
  const [state, setState] = useState(getDeviceToken() ? STATES.IDLE : STATES.PAIRING);
  const [meta, setMeta] = useState(getDeviceMeta());
  const [employees, setEmployees] = useState([]);
  const [pickedEmployee, setPickedEmployee] = useState(null);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [now, setNow] = useState(new Date());
  const [showUnpairConfirm, setShowUnpairConfirm] = useState(false);
  // Banner shown when a submit is blocked because the operator's picked
  // employee no longer exists in the latest roster (e.g. PE re-synced).
  const [rosterStaleMsg, setRosterStaleMsg] = useState("");
  // Track the active device token's identity so we can wipe per-session
  // picks if the device gets re-paired without a full page reload.
  const tokenIdentityRef = useRef(getDeviceToken() || null);
  const eventIdRef = useRef(null);
  const timerRef = useRef(null);

  // When the device token changes (re-pair, unpair, token-rejected reset),
  // drop any stale picked employee. The token claims own outlet/org, so
  // a new token implies a fresh employee universe.
  useEffect(() => {
    const i = setInterval(() => {
      const cur = getDeviceToken() || null;
      if (cur !== tokenIdentityRef.current) {
        tokenIdentityRef.current = cur;
        setPickedEmployee(null);
        setEmployees([]);
        setRosterStaleMsg("");
      }
    }, 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    if (state === STATES.PAIRING || state === STATES.TOKEN_REJECTED) return;
    deviceApi.get("/employees/public")
      .then((r) => {
        const next = Array.isArray(r.data) ? r.data : [];
        setEmployees(next);
        // Roster refresh ALWAYS clears any in-flight selection. If the
        // operator already picked an employee that is no longer in the
        // fresh roster (PE has updated outlet/employee mappings since),
        // the next submit attempt must force a re-pick. We also wipe the
        // staleness banner.
        setPickedEmployee((prev) => {
          if (!prev) return null;
          return next.some((e) => e.id === prev.id) ? prev : null;
        });
        setRosterStaleMsg("");
      })
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 423) {
          // Token revoked by the manager — clear and re-pair (token won't
          // come back).
          clearDeviceSession();
          setMeta(null);
          setEmployees([]);
          setPickedEmployee(null);
          setState(STATES.PAIRING);
          return;
        }
        if (status === 401) {
          // Was this token freshly minted by People Engine seconds ago?
          // If yes, the verifier just rejected a brand-new PE-issued token —
          // a config mismatch between PE and Presence, NOT a normal expiry.
          // Surface this visibly. Do NOT wipe the token (admin needs evidence).
          const ts = parseInt(localStorage.getItem("presence_just_paired_at") || "0", 10);
          if (ts && Date.now() - ts < JUST_PAIRED_WINDOW_MS) {
            setState(STATES.TOKEN_REJECTED);
            return;
          }
          // Older token that has stopped working — silently re-pair (normal).
          clearDeviceSession();
          setMeta(null);
          setEmployees([]);
          setPickedEmployee(null);
          setState(STATES.PAIRING);
        }
      });
  }, [state]);

  const handlePaired = ({ outletName, deviceLabel, pairedAtIso }) => {
    // Mark the moment of successful PE redeem so a subsequent 401 from
    // Presence can be distinguished from a normal expired/revoked token.
    try { localStorage.setItem("presence_just_paired_at", String(Date.now())); } catch (_) { /* ignore */ }
    tokenIdentityRef.current = getDeviceToken() || null;
    setMeta({ outlet_name: outletName, device_label: deviceLabel, paired_at_iso: pairedAtIso });
    setEmployees([]);
    setPickedEmployee(null);
    setRosterStaleMsg("");
    setState(STATES.IDLE);
  };

  const retryAfterRejection = () => {
    // Re-attempt the employees fetch with the same token (in case the admin
    // just fixed the Presence-side config and asked us to retry).
    try { localStorage.setItem("presence_just_paired_at", String(Date.now())); } catch (_) { /* ignore */ }
    setPickedEmployee(null);
    setRosterStaleMsg("");
    setState(STATES.IDLE);
  };

  const clearPairingAfterRejection = () => {
    try { localStorage.removeItem("presence_just_paired_at"); } catch (_) { /* ignore */ }
    clearDeviceSession();
    tokenIdentityRef.current = null;
    setMeta(null);
    setEmployees([]);
    setPickedEmployee(null);
    setRosterStaleMsg("");
    setState(STATES.PAIRING);
  };

  const unpair = async () => {
    setShowUnpairConfirm(true);
  };

  const confirmUnpair = async () => {
    try { await deviceApi.post("/people-engine/unpair", {}); } catch (_) { /* ignore */ }
    clearDeviceSession();
    tokenIdentityRef.current = null;
    setMeta(null);
    setEmployees([]);
    setPickedEmployee(null);
    setRosterStaleMsg("");
    setShowUnpairConfirm(false);
    setState(STATES.PAIRING);
  };

  const resetIdle = () => {
    clearTimeout(timerRef.current);
    setResult(null);
    setLogs([]);
    setPickedEmployee(null);
    setRosterStaleMsg("");
    setState(STATES.IDLE);
  };

  const startScanFlow = () => {
    if (!online) {
      setResult({
        status: "QUEUED",
        message: "Offline. We'll capture and sync when the connection returns.",
        confidence_tier: "OFFLINE",
      });
      setState(STATES.RESULT);
      timerRef.current = setTimeout(resetIdle, 4000);
      return;
    }
    setState(STATES.SELECT);
  };

  const pushLog = (line) =>
    setLogs((prev) => [...prev, { t: Date.now(), line }].slice(-8));

  const pickEmployee = (employee) => {
    setPickedEmployee(employee);
    setState(STATES.CAPTURE);
  };

  const runScan = async (selfieDataUrl) => {
    const employee = pickedEmployee;
    // PILOT GUARD (P0): the operator's picked employee MUST exist in the
    // latest /employees/public response. If not, block submit, refetch the
    // roster, and force the operator back to the selection step. Prevents
    // the kiosk from POSTing a stale employee_id that PE no longer knows.
    const inRoster = employee && employees.some((e) => e.id === employee.id);
    if (!inRoster) {
      setRosterStaleMsg("Employee roster changed. Refreshing employees.");
      setPickedEmployee(null);
      try {
        const r = await deviceApi.get("/employees/public");
        setEmployees(Array.isArray(r.data) ? r.data : []);
      } catch (_) { /* let the existing roster effect retry */ }
      setState(STATES.SELECT);
      return;
    }

    setState(STATES.SCANNING);
    setLogs([]);
    const entries = [
      "camera:release",
      "selfie:compress",
      "selfie:upload",
      "face-engine:lookup",
      "people-engine:record",
    ];
    for (let i = 0; i < entries.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => setTimeout(res, 180 + Math.random() * 140));
      pushLog(entries[i]);
    }
    try {
      const tokenSub = readTokenSub();
      const capturedAtIso = new Date().toISOString();
      // Idempotency key: one id per capture attempt, reused if the operator
      // retries the same capture after a network failure. People Engine
      // de-duplicates on this value, so a retry never double-punches.
      if (!eventIdRef.current) {
        eventIdRef.current = `pev_${(crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(16).slice(2)).replace(/-/g, "")}`;
      }
      // TEMPORARY pilot debug logging (P0). Remove or downgrade to debug-only
      // once the kiosk submit is verified end-to-end with the live PE outlet.
      // No PII beyond what the operator UI already shows.
      // eslint-disable-next-line no-console
      console.log("[Presence][scan-submit]", {
        endpoint: "/api/attendance/terminal/scan",
        employee_display_name: employee?.display_name || employee?.name,
        employee_id: employee?.id,
        device_id: tokenSub,
        outlet_id: meta?.outlet_id || "(server-derived from token)",
        scan_type: "TERMINAL",
        captured_at_iso: capturedAtIso,
      });
      const res = await deviceApi.post("/attendance/terminal/scan", {
        device_id: tokenSub,
        outlet_id: undefined,
        captured_at_iso: capturedAtIso,
        claimed_employee_id: employee?.id || null,
        client_event_id: eventIdRef.current,
        selfie: selfieDataUrl || undefined,
        capture_context: { camera: "front", app_version: "1.0.0", network_status: "online" },
      });
      setResult(res.data);
      eventIdRef.current = null;
      setState(STATES.RESULT);
      const dwell = res.data.status === "SUCCESS" ? 3500 : 6000;
      timerRef.current = setTimeout(resetIdle, dwell);
    } catch (e) {
      if (e?.response?.status === 423 || e?.response?.status === 401) {
        clearDeviceSession();
        tokenIdentityRef.current = null;
        setMeta(null);
        setEmployees([]);
        setPickedEmployee(null);
        setState(STATES.PAIRING);
        return;
      }
      // Server still might return 404 "Employee not found" if PE removed
      // the employee between our last roster refresh and the submit. Same
      // remediation: refetch roster, force re-pick.
      if (e?.response?.status === 404
          && /employee/i.test(e?.response?.data?.detail || "")) {
        setRosterStaleMsg("Employee roster changed. Refreshing employees.");
        setPickedEmployee(null);
        try {
          const r = await deviceApi.get("/employees/public");
          setEmployees(Array.isArray(r.data) ? r.data : []);
        } catch (_) { /* let the existing roster effect retry */ }
        setState(STATES.SELECT);
        return;
      }
      setResult({ status: "REJECTED", message: "Capture failed. Try again.", confidence_tier: "FAILED", confidence_score: 0, reason_codes: ["NETWORK_ERROR"], face_status: "FAILED" });
      setState(STATES.RESULT);
      timerRef.current = setTimeout(resetIdle, 4500);
    }
  };

  if (state === STATES.PAIRING) {
    return <PairingScreen online={online} onPaired={handlePaired} />;
  }

  if (state === STATES.TOKEN_REJECTED) {
    return (
      <TokenRejectedScreen
        onRetry={retryAfterRejection}
        onClear={clearPairingAfterRejection}
      />
    );
  }

  if (state === STATES.ENROLL) {
    return <FaceEnrollmentFlow onClose={() => setState(STATES.IDLE)} />;
  }

  return (
    <div className="kiosk-root" data-testid="terminal-root">
      <div className="flex items-center h-14 px-6 border-b border-neutral-200">
        <div className="flex items-center gap-3" data-testid="terminal-brand">
          <BrandMark size={22} />
          <span className="mono-label">Presence</span>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <span className="mono-label" data-testid="terminal-device-label">{meta?.device_label || "Presence"}</span>
          <span className="mono-label">·</span>
          <span className="mono-label" data-testid="terminal-outlet-name">{meta?.outlet_name}</span>
          <span className="mono-label">·</span>
          <span className="mono-value text-xs" data-testid="terminal-clock">
            {now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </span>
          <span className={`mono-label inline-flex items-center gap-1 ${online ? "text-green-700" : "text-red-700"}`} data-testid="terminal-net">
            {online ? <Wifi size={12} /> : <WifiOff size={12} />} {online ? "Online" : "Offline"}
          </span>
          <button
            data-testid="btn-unpair"
            onClick={unpair}
            className="mono-label inline-flex items-center gap-1 text-neutral-500 hover:text-red-600"
            title="Unpair this device from its outlet"
          >
            <LogOut size={12} /> Unpair device
          </button>
        </div>
      </div>

      <div className="w-full bg-[#FEF9C3] border-b border-[#EAB308] px-6 py-2 text-center">
        <span className="mono-label text-[#854D0E]" style={{ fontSize: 10 }}>
          PAIRED TO {meta?.outlet_name?.toUpperCase() || "PEOPLE ENGINE"} · READY TO CAPTURE ATTENDANCE
        </span>
      </div>

      {state === STATES.IDLE && <IdleScreen onStart={startScanFlow} onEnroll={() => setState(STATES.ENROLL)} employees={employees} outletName={meta?.outlet_name} />}
      {state === STATES.SELECT && <SelectIdentityScreen employees={employees} onPick={pickEmployee} onCancel={resetIdle} staleMsg={rosterStaleMsg} />}
      {state === STATES.CAPTURE && <CaptureScreen employee={pickedEmployee} onCapture={runScan} onCancel={resetIdle} />}
      {state === STATES.SCANNING && <ScanningScreen logs={logs} />}
      {state === STATES.RESULT && <ResultScreen result={result} onReset={resetIdle} />}

      {showUnpairConfirm && (
        <UnpairConfirm
          outletName={meta?.outlet_name}
          onCancel={() => setShowUnpairConfirm(false)}
          onConfirm={confirmUnpair}
        />
      )}
    </div>
  );
}

function readTokenSub() {
  const t = getDeviceToken();
  if (!t) return null;
  try {
    const payload = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.sub;
  } catch (_) { return null; }
}

function IdleScreen({ onStart, onEnroll, employees, outletName }) {
  return (
    <div className="grid grid-cols-12 gap-0 min-h-[calc(100vh-7rem)]">
      <div className="col-span-12 md:col-span-8 p-10 md:p-16 flex flex-col justify-center">
        <div className="mono-label" data-testid="idle-status-label">READY TO CAPTURE ATTENDANCE</div>
        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter mt-4">
          {outletName || "Presence"}.
        </h1>
        <p className="mt-6 text-xl text-neutral-600 max-w-xl">
          Tap below and look into the camera. Under 3 seconds.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <button
            data-testid="btn-start-scan"
            onClick={onStart}
            className="h-20 px-10 bg-black text-white font-heading text-xl font-bold tracking-wide uppercase border border-black hover:bg-neutral-800 inline-flex items-center gap-3"
          >
            <Camera size={22} /> Mark attendance
          </button>
          <button
            data-testid="btn-enroll-face"
            onClick={onEnroll}
            className="h-20 px-8 bg-white text-black border border-black font-mono text-sm uppercase tracking-wider hover:bg-neutral-100 inline-flex items-center gap-3"
          >
            <UserPlus size={18} /> Enrol a face
          </button>
        </div>
        <div className="mt-10 max-w-xl border border-neutral-200 bg-[#FAFAFA] p-4">
          <div className="mono-label text-neutral-600">IF CAPTURE FAILS</div>
          <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
            Try again. Anything that needs review is resolved by your manager
            inside People Engine — never on this device.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-4 gap-4 max-w-xl">
          {employees.slice(0, 4).map((e) => (
            <div key={e.id} className="text-center">
              <img src={e.photo_url} alt={e.name} className="w-full aspect-square object-cover grayscale border border-neutral-200" />
              <div className="mono-label mt-2 truncate" style={{ fontSize: 9 }}>{e.code}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="col-span-12 md:col-span-4 bg-black text-white p-10 md:p-12 flex flex-col">
        <div className="mono-label text-neutral-400">CAMERA FRAME</div>
        <div className="flex-1 relative mt-4 border border-neutral-800 bg-neutral-950 overflow-hidden min-h-[260px]">
          <CornerBrackets />
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/6f1a01bc-6d96-4288-a648-39b8d288a185/images/5d94da3f48f6474b579675361ab8112c78d32c633ef86cbfad94efe1be34b829.png"
              alt="mesh"
              className="opacity-40 max-h-full"
            />
          </div>
          <div className="absolute bottom-3 left-3 mono-label text-neutral-500" style={{ fontSize: 9 }}>
            READY
          </div>
        </div>
        <div className="mt-6 space-y-2 font-mono text-xs text-neutral-400">
          <div>&gt; idle: awaiting subject</div>
          <div>&gt; liveness: off</div>
          <div>&gt; spoof-check: armed</div>
          <div>&gt; auth: capture-only</div>
          <div className="caret-blink">&gt; </div>
        </div>
      </div>
    </div>
  );
}

function SelectIdentityScreen({ employees, onPick, onCancel, staleMsg }) {
  return (
    <div className="p-8 md:p-12 min-h-[calc(100vh-7rem)]">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono-label">STEP 01 / 03</div>
          <h2 className="font-heading text-4xl font-black tracking-tighter mt-2" data-testid="select-title">Who is marking attendance?</h2>
          <p className="text-neutral-600 mt-2 max-w-xl">
            Tap your name. We&apos;ll then capture a quick face photo and send
            it to People Engine.
          </p>
        </div>
        <button onClick={onCancel} data-testid="btn-cancel-select" className="h-12 px-5 border border-neutral-300 font-mono text-xs uppercase tracking-wider hover:bg-neutral-100">Cancel</button>
      </div>
      {staleMsg && (
        <div
          data-testid="roster-stale-banner"
          className="mt-6 max-w-2xl border-2 border-[#EAB308] bg-[#FEF9C3] p-4 font-mono text-sm text-[#854D0E]"
        >
          {staleMsg}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-8">
        {employees.map((e) => (
          <button
            key={e.id}
            data-testid={`select-employee-${e.code}`}
            onClick={() => onPick(e)}
            className="group border border-neutral-200 bg-white p-3 text-left hover:border-black transition-colors"
          >
            <div className="relative aspect-square overflow-hidden bg-neutral-100">
              <img src={e.photo_url} alt={e.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition" />
              <span className="absolute top-2 left-2 mono-label bg-white px-1.5 py-0.5" style={{ fontSize: 9 }}>{e.code}</span>
              <ProfileChip profile={e.confidence_profile} />
            </div>
            <div className="mt-3">
              <div className="font-heading font-bold text-lg leading-tight">{e.name}</div>
              <div className="mono-label mt-1" style={{ fontSize: 9 }}>{e.role}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CaptureScreen({ employee, onCapture, onCancel }) {
  return (
    <div className="p-8 md:p-12 min-h-[calc(100vh-7rem)]" data-testid="capture-screen">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono-label">STEP 02 / 03 · CAPTURE FACE</div>
          <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mt-2 leading-[1]" data-testid="capture-title">
            Look at the camera{employee?.display_name ? `, ${employee.display_name}` : ""}.
          </h2>
          <p className="text-neutral-600 mt-3 max-w-xl">
            Centre your face in the frame. We capture one photo and send it to
            People Engine.
          </p>
        </div>
        <button
          onClick={onCancel}
          data-testid="btn-cancel-capture"
          className="h-12 px-5 border border-neutral-300 font-mono text-xs uppercase tracking-wider hover:bg-neutral-100"
        >
          Cancel
        </button>
      </div>
      <div className="mt-10 grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 md:col-span-7">
          <WebcamCapture
            prompt="Look at the camera"
            actionLabel="Capture face"
            onCapture={onCapture}
            onCancel={onCancel}
            testIdPrefix="punch-cam"
          />
        </div>
        <div className="col-span-12 md:col-span-5 space-y-4">
          {employee?.photo_url ? (
            <img
              src={employee.photo_url}
              alt={employee.name}
              className="w-40 aspect-square object-cover grayscale border border-neutral-200"
            />
          ) : null}
          {employee && (
            <div>
              <div className="mono-label">CAPTURING FOR</div>
              <div className="font-heading text-2xl font-bold tracking-tight mt-1">{employee.name}</div>
              <div className="mono-label mt-1">{employee.code} · {employee.role}</div>
            </div>
          )}
          <div className="border border-neutral-200 bg-[#FAFAFA] p-4 text-sm text-neutral-700 leading-relaxed max-w-md">
            <div className="mono-label text-neutral-600 mb-2">REMINDER</div>
            This terminal only captures attendance. The capture goes to
            People Engine — decisions and identity matching live there.
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileChip({ profile }) {
  const map = { HIGH: "bg-green-100 text-green-800", MEDIUM: "bg-yellow-100 text-yellow-800", LOW: "bg-red-100 text-red-800", MIXED: "bg-neutral-200 text-neutral-800" };
  return (
    <span className={`absolute top-2 right-2 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider ${map[profile] || map.MIXED}`}>
      sim: {profile}
    </span>
  );
}

function ScanningScreen({ logs }) {
  return (
    <div className="grid grid-cols-12 min-h-[calc(100vh-7rem)]">
      <div className="col-span-12 md:col-span-7 bg-black text-white p-8 relative overflow-hidden">
        <div className="mono-label text-neutral-400">CAMERA / LIVE</div>
        <div className="mt-4 relative border border-neutral-800 h-[85%] overflow-hidden">
          <CornerBrackets />
          <img
            src="https://static.prod-images.emergentagent.com/jobs/6f1a01bc-6d96-4288-a648-39b8d288a185/images/5d94da3f48f6474b579675361ab8112c78d32c633ef86cbfad94efe1be34b829.png"
            alt="scanning"
            className="absolute inset-0 w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 pointer-events-none">
            <div className="scanline" />
          </div>
        </div>
      </div>
      <div className="col-span-12 md:col-span-5 p-8 md:p-12 bg-white">
        <div className="mono-label">VERIFYING / FACE TRUST SIMULATION</div>
        <h2 className="font-heading text-4xl font-black mt-3 tracking-tighter bracket-pulse" data-testid="scanning-heading">Hold still.</h2>
        <p className="mt-3 text-neutral-600">Acquiring frames, probing liveness, computing confidence.</p>
        <div className="mt-8 border border-neutral-200 bg-[#FAFAFA] p-4 h-72 overflow-hidden font-mono text-xs space-y-1">
          {logs.map((l, i) => (
            <div key={i} className="log-line text-neutral-700">
              <span className="text-neutral-400">[{new Date(l.t).toLocaleTimeString("en-GB")}]</span>{" "}
              &gt; {l.line}
            </div>
          ))}
          {logs.length === 0 && <div className="text-neutral-400">&gt; initialising…</div>}
          <div className="caret-blink text-black">&gt; </div>
        </div>
      </div>
    </div>
  );
}

function ResultScreen({ result, onReset }) {
  const tier = result?.confidence_tier || "FAILED";
  const isSuccess = result?.status === "SUCCESS";
  const isQueued = result?.status === "QUEUED";
  const isReview = result?.status === "REVIEW";
  const isRetry = result?.status === "RETRY" || result?.status === "REJECTED";

  const tone = isSuccess
    ? { head: "Captured", bg: "bg-[#DCFCE7]", border: "border-[#22C55E]", text: "text-[#166534]" }
    : isReview
    ? { head: "Review required in People Engine", bg: "bg-[#FEF9C3]", border: "border-[#EAB308]", text: "text-[#854D0E]" }
    : isQueued
    ? { head: "Pending sync", bg: "bg-neutral-100", border: "border-neutral-400", text: "text-neutral-800" }
    : { head: "Rejected", bg: "bg-[#FEE2E2]", border: "border-[#EF4444]", text: "text-[#991B1B]" };

  // Honest pilot wording for face status. ML is mocked — never say "verified".
  const faceStatus = result?.face_status;
  const faceCopy =
    faceStatus === "MOCK_MATCH" || faceStatus === "VERIFIED" || faceStatus === "LOW_CONFIDENCE"
      ? { label: "Face captured", tint: "border-[#166534] text-[#166534] bg-[#DCFCE7]" }
      : faceStatus === "NO_FACE_PROFILE"
      ? { label: "Face profile missing", tint: "border-[#854D0E] text-[#854D0E] bg-[#FEF9C3]" }
      : faceStatus === "FAILED"
      ? { label: "Face capture failed — try again", tint: "border-[#991B1B] text-[#991B1B] bg-[#FEE2E2]" }
      : null;

  return (
    <div className={`${isSuccess ? "success-flash" : ""} min-h-[calc(100vh-7rem)] p-8 md:p-16 flex items-center justify-center`}>
      <div className={`w-full max-w-5xl border-2 ${tone.border} ${tone.bg} p-10 md:p-14`}>
        <div className="grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 md:col-span-4">
            {result?.employee_photo_url ? (
              <img
                src={result.employee_photo_url}
                alt=""
                data-testid="result-photo"
                className="w-full aspect-square object-cover border border-neutral-900"
              />
            ) : (
              <div className="w-full aspect-square bg-white border border-neutral-900 flex items-center justify-center">
                <Camera size={48} strokeWidth={1} className="text-neutral-400" />
              </div>
            )}
          </div>
          <div className="col-span-12 md:col-span-8">
            <div className={`mono-label ${tone.text}`}>{tone.head.toUpperCase()}</div>
            <h2 className="font-heading text-5xl md:text-6xl font-black tracking-tighter mt-3" data-testid="result-title">
              {result?.employee_display_name
                ? `${tone.head === "Captured" ? "Hi" : "Hello"}, ${result.employee_display_name}.`
                : tone.head}
            </h2>
            <p className="mt-4 text-xl text-neutral-700" data-testid="result-message">{result?.message}</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <ConfidenceBadge tier={tier} score={result?.confidence_score} />
              {faceCopy && (
                <span
                  data-testid="result-face-status"
                  className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-[0.18em] font-semibold ${faceCopy.tint}`}
                >
                  {faceCopy.label}
                </span>
              )}
              {result?.pe_delivery && result.pe_delivery !== "NOT_SENT" && (
                <span
                  data-testid="result-pe-delivery"
                  className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-[0.18em] font-semibold ${
                    result.pe_delivery === "DELIVERED"
                      ? "border-neutral-900 text-neutral-900"
                      : "border-amber-600 text-amber-700 bg-amber-50"
                  }`}
                >
                  {result.pe_delivery === "DELIVERED"
                    ? "Sent to People Engine"
                    : "Not yet delivered · will retry"}
                </span>
              )}
              {result?.attendance_action && (
                <span className="px-2 py-1 border border-neutral-900 font-mono text-[10px] uppercase tracking-[0.18em] font-semibold">
                  {result.attendance_action.replace("_", " ")}
                </span>
              )}
              {result?.marked_at_iso && (
                <span className="mono-label">
                  @ {new Date(result.marked_at_iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              {result?.decision && (
                <span className="mono-label">decision: {result.decision.replace(/_/g, " ").toLowerCase()}</span>
              )}
            </div>
            {result?.reason_codes?.length > 0 && (
              <div className="mt-4 font-mono text-xs text-neutral-600">
                reason: {result.reason_codes.join(" · ")}
              </div>
            )}
            {(isReview || isRetry) && (
              <div className="mt-6 border border-neutral-900 bg-white p-4 max-w-2xl">
                <div className="mono-label">NEXT STEP</div>
                <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
                  This terminal only captures attendance. Your capture is on
                  file in the People Engine — speak to your manager if it
                  needs review.
                </p>
              </div>
            )}
            <div className="mt-10 flex flex-wrap gap-3">
              <button data-testid="btn-done" onClick={onReset} className="h-14 px-8 bg-black text-white font-mono text-sm uppercase tracking-wider inline-flex items-center gap-2">
                Done
              </button>
              {(isRetry || isReview) && (
                <button data-testid="btn-retry" onClick={onReset} className="h-14 px-8 border border-neutral-900 bg-white font-mono text-sm uppercase tracking-wider inline-flex items-center gap-2">
                  <RotateCcw size={16} /> Try again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CornerBrackets() {
  const common = "absolute w-6 h-6 border-[#22C55E]";
  return (
    <>
      <span className={`${common} top-2 left-2 border-l-2 border-t-2`} />
      <span className={`${common} top-2 right-2 border-r-2 border-t-2`} />
      <span className={`${common} bottom-2 left-2 border-l-2 border-b-2`} />
      <span className={`${common} bottom-2 right-2 border-r-2 border-b-2`} />
    </>
  );
}

function TokenRejectedScreen({ onRetry, onClear }) {
  return (
    <div
      className="kiosk-root min-h-screen bg-white flex items-center justify-center px-6"
      data-testid="token-rejected-root"
    >
      <div className="w-full max-w-2xl">
        <div className="border-2 border-[#EF4444] bg-[#FEE2E2] p-8 md:p-10">
          <div className="mono-label text-[#991B1B]" style={{ fontSize: 10 }}>
            PRESENCE · DEVICE TOKEN REJECTED
          </div>
          <h1
            className="font-heading text-4xl md:text-5xl font-black tracking-tighter mt-3 leading-[1] text-[#991B1B]"
            data-testid="token-rejected-title"
          >
            Pairing accepted, but this device token was rejected.
          </h1>
          <p className="mt-5 text-[#7F1D1D] max-w-xl">
            People Engine accepted the pairing code and issued a token, but
            Presence could not verify it. This usually means the Presence and
            People Engine deployments are out of sync. Please contact admin.
          </p>
          <p className="mt-3 text-sm text-[#7F1D1D]/80 max-w-xl">
            No employee data is loaded; no attendance can be captured until
            this is resolved.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onRetry}
              data-testid="token-rejected-retry"
              className="h-14 px-6 bg-black text-white font-heading font-bold tracking-wide uppercase border border-black hover:bg-neutral-800"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={onClear}
              data-testid="token-rejected-clear"
              className="h-14 px-6 border-2 border-[#991B1B] bg-white text-[#991B1B] font-mono text-sm uppercase tracking-wider hover:bg-[#FEE2E2]"
            >
              Clear pairing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



function PairingScreen({ online, onPaired }) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!PEOPLE_ENGINE_URL) {
      setError("Pairing is not configured. Set REACT_APP_PEOPLE_ENGINE_URL and reload.");
      return;
    }
    setSubmitting(true);
    try {
      const fp = (() => {
        try {
          const ex = localStorage.getItem("presence_fingerprint");
          if (ex) return ex;
          const v = "fp_" + Math.random().toString(36).slice(2, 10) + "_" + Date.now().toString(36);
          localStorage.setItem("presence_fingerprint", v);
          return v;
        } catch (_) { return "fp_anon"; }
      })();
      // Truth contract: pairing is owned by People Engine. We post directly
      // to the PE host; PE validates the code, mints a presence_device_token
      // (HS256, iss=people_engine, aud=presence), and returns it to us.
      const r = await axios.post(
        `${PEOPLE_ENGINE_URL}/api/presence/pair-device`,
        {
          pairing_code: code.trim().toUpperCase(),
          device_fingerprint: fp,
          device_label: label.trim() || "Presence",
        },
      );
      const { presence_device_token, device_id, outlet_name } = r.data;
      if (!presence_device_token) {
        throw new Error("People Engine did not return a token");
      }
      const paired_at_iso = new Date().toISOString();
      setDeviceSession(
        presence_device_token,
        { name: outlet_name },
        { device_name: label.trim() || "Presence", device_id },
      );
      localStorage.setItem("device_outlet", JSON.stringify({
        outlet_name,
        device_label: label.trim() || "Presence",
        device_id,
        paired_at_iso,
      }));
      onPaired({
        outletName: outlet_name,
        deviceLabel: label.trim() || "Presence",
        pairedAtIso: paired_at_iso,
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 404) setError("Pairing code not recognised.");
      else if (status === 410) setError(typeof detail === "string" ? detail : "This pairing code is no longer valid.");
      else if (status === 400) setError("Enter a valid pairing code.");
      else if (status === 401 || status === 403) setError("Pairing was refused by People Engine.");
      else if (!err?.response) setError("Could not reach People Engine. Check connectivity.");
      else setError(typeof detail === "string" ? detail : "Pairing failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kiosk-root flex items-center justify-center min-h-screen bg-white" data-testid="pairing-root">
      <div className="w-full max-w-2xl p-10 md:p-16">
        <div className="flex items-center gap-3">
          <BrandMark size={28} />
          <span className="mono-label">Presence · capture-only</span>
        </div>
        <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tighter mt-8 leading-[0.95]" data-testid="pairing-title">
          Pair this Presence device.
        </h1>
        <p className="mt-5 text-neutral-700 max-w-lg" data-testid="pairing-subtitle">
          Enter the pairing code generated in People Engine for this outlet.
        </p>

        <form onSubmit={submit} className="mt-10" data-testid="pairing-form">
          <label className="mono-label">PAIRING CODE</label>
          <input
            data-testid="pairing-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
            autoFocus
            inputMode="text"
            autoComplete="off"
            placeholder="ABC123"
            maxLength={6}
            className="mt-2 w-full h-20 px-5 border-2 border-black bg-white font-mono text-4xl tracking-[0.4em] uppercase focus:outline-none"
          />

          <label className="mono-label mt-6 block">DEVICE LABEL (OPTIONAL)</label>
          <input
            data-testid="pairing-label-input"
            value={label}
            onChange={(e) => setLabel(e.target.value.slice(0, 40))}
            placeholder="Counter tablet"
            className="mt-2 w-full h-12 px-3 border border-neutral-300 bg-white font-mono focus:outline-none focus:border-black"
          />

          {error && (
            <div className="mt-4 text-red-700 font-mono text-sm" data-testid="pairing-error">
              {error}
            </div>
          )}
          {!online && (
            <div className="mt-4 text-amber-700 font-mono text-sm" data-testid="pairing-offline">
              Offline — connect to a network to pair.
            </div>
          )}

          <button
            type="submit"
            data-testid="pairing-submit"
            disabled={submitting || code.length < 4 || !online}
            className="mt-8 h-16 px-10 bg-black text-white font-heading text-lg font-bold tracking-wide uppercase border border-black hover:bg-neutral-800 inline-flex items-center gap-3 disabled:opacity-40"
          >
            <KeyRound size={20} /> {submitting ? "Pairing…" : "Pair device"}
          </button>
        </form>

        <p className="mt-8 text-sm text-neutral-600 max-w-lg" data-testid="pairing-helper">
          Presence only captures attendance. Employee and payroll decisions
          happen in People Engine.
        </p>
      </div>
    </div>
  );
}

function UnpairConfirm({ outletName, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="unpair-confirm-modal"
    >
      <div className="w-full max-w-lg bg-white border border-neutral-900 p-8 m-4">
        <div className="mono-label text-red-600" style={{ fontSize: 10 }}>
          UNPAIR DEVICE
        </div>
        <h2 className="font-heading text-3xl font-black tracking-tighter mt-3">
          Unpair from {outletName || "this outlet"}?
        </h2>
        <p className="mt-4 text-sm text-neutral-700 leading-relaxed">
          Unpairing removes this device from the current outlet. To use
          Presence again, pair it with a new People Engine code.
        </p>
        <div className="mt-8 flex gap-3 justify-end">
          <button
            onClick={onCancel}
            data-testid="btn-unpair-cancel"
            className="h-12 px-5 border border-neutral-300 font-mono text-xs uppercase tracking-wider hover:bg-neutral-100"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            data-testid="btn-unpair-confirm"
            className="h-12 px-5 bg-red-600 text-white font-mono text-xs uppercase tracking-wider hover:bg-red-700"
          >
            Unpair device
          </button>
        </div>
      </div>
    </div>
  );
}
